<template>
    <div class="w-full">
        <!-- Buttons above the slider -->
        <div class="flex justify-between p-4">
            <primary-button
                @click="$router.push({ name: 'home-shop' })"
                class="w-full md:w-auto m-3"
                color="red"
            >
                {{ $translate('public.shop.home.bikes.card.back') }}
            </primary-button>
            <primary-button
                @click="
                    $router.push({ name: 'accessories-catalog-categories' })
                "
                class="w-full md:w-auto m-3"
                color="red"
            >
                {{ $translate('public.shop.home.bikes.card.redirect') }}
            </primary-button>
        </div>

        <!-- Bike categories slider -->
        <div class="relative flex items-center justify-center bg-red p-8">
            <button
                class="absolute left-2 md:left-4 text-white"
                @click="prevSlide"
            >
                <i class="fa-solid fa-chevron-left fa-2x"></i>
            </button>

            <div class="overflow-hidden w-full">
                <h2 class="text-red font-bold text-lg mb-3 text-left">
                    {{ $translate('public.shop.home.title') }}
                </h2>
                <div
                    class="flex transition-transform duration-500 ease-in-out"
                    :style="{ transform: `translateX(-${slideIndex * 208}px)` }"
                >
                    <div
                        v-for="(bike, index) in bikeCategories"
                        :key="index"
                        class="flex-shrink-0 w-[208px] cursor-pointer text-center mx-2"
                        @click="updateQuery(bike.name, 'all')"
                    >
                        <div
                            class="w-52 h-52 bg-center bg-cover rounded-xl"
                            :class="bike.class"
                        ></div>
                        <h3 class="text-white mt-3 text-base">
                            {{ $translate(bike.translationKey) }}
                        </h3>
                    </div>
                </div>
            </div>

            <button
                class="absolute right-2 md:right-4 text-white"
                @click="nextSlide"
            >
                <i class="fa-solid fa-chevron-right fa-2x"></i>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';

const router = useRouter();
const emits = defineEmits(['isLoading']);

const slideIndex = ref(0);
let sliderInterval = null;

const bikeCategories = [
    {
        name: 'ville',
        translationKey: 'public.shop.home.slider.card.ville',
        class: 'bg-card1',
    },
    {
        name: 'Ville et chemin',
        translationKey: 'public.shop.home.slider.card.ville-et-chemin',
        class: 'bg-card2',
    },
    {
        name: 'Tout chemin',
        translationKey: 'public.shop.home.slider.card.tout-chemin',
        class: 'bg-card3',
    },
    {
        name: 'VTT',
        translationKey: 'public.shop.home.slider.card.vtt',
        class: 'bg-card4',
    },
    {
        name: 'Route',
        translationKey: 'public.shop.home.slider.card.route',
        class: 'bg-card5',
    },
    {
        name: 'Gravel',
        translationKey: 'public.shop.home.slider.card.gravel',
        class: 'bg-card6',
    },
    {
        name: 'Speed Pedelec',
        translationKey: 'public.shop.home.slider.card.speedpedelec',
        class: 'bg-card7',
    },
    {
        name: 'Cargo',
        translationKey: 'public.shop.home.slider.card.cargo',
        class: 'bg-card8',
    },
    {
        name: 'Pliant',
        translationKey: 'public.shop.home.slider.card.pliant',
        class: 'bg-card9',
    },
    {
        name: 'BMX',
        translationKey: 'public.shop.home.slider.card.bmx',
        class: 'bg-card10',
    },
    {
        name: 'Enfant',
        translationKey: 'public.shop.home.slider.card.enfant',
        class: 'bg-card11',
    },
    {
        name: 'Trottinette',
        translationKey: 'public.shop.home.slider.card.trottinette',
        class: 'bg-card12',
    },
];

const updateQuery = (utilisation, promotion) => {
    const query = {
        utilisation: encodeURIComponent(utilisation),
        promotion: encodeURIComponent(promotion),
        brand: 'all',
        size: 'all',
        frame_type: 'all',
        electric: 'all',
        stock: 'all',
        engine: 'all',
        battery: 'all',
    };

    router.push({ query });
    emits(
        'isLoading',
        {
            utilisation: [utilisation],
            promotion: ['all'],
            brand: ['all'],
            size: ['all'],
            frame_type: ['all'],
            electric: ['all'],
            stock: ['all'],
            engine: ['all'],
            battery: ['all'],
        },
        null,
    );
};

// Move slides
const nextSlide = () => {
    const maxIndex =
        bikeCategories.length - Math.floor(window.innerWidth / 208);

    if (slideIndex.value > maxIndex) {
        slideIndex.value = 0;
    } else {
        slideIndex.value++;
    }
};
const prevSlide = () => {
    slideIndex.value =
        (slideIndex.value - 1 + bikeCategories.length) % bikeCategories.length;
};

// Auto-slide
const startAutoSlide = () => {
    sliderInterval = setInterval(nextSlide, 3000);
};
onMounted(startAutoSlide);
onUnmounted(() => clearInterval(sliderInterval));
</script>

<style>
/* Tailwind doesn't support dynamic classes for background images, so define them here */
.bg-card1 {
    background-image: url('/storage/assets/images/covers/bikes/1.jpg');
}
.bg-card2 {
    background-image: url('/storage/assets/images/covers/bikes/2.jpg');
}
.bg-card3 {
    background-image: url('/storage/assets/images/covers/bikes/3.jpg');
}
.bg-card4 {
    background-image: url('/storage/assets/images/covers/bikes/4.jpg');
}
.bg-card5 {
    background-image: url('/storage/assets/images/covers/bikes/5.jpg');
}
.bg-card6 {
    background-image: url('/storage/assets/images/covers/bikes/6.jpg');
}
.bg-card7 {
    background-image: url('/storage/assets/images/covers/bikes/7.jpg');
}
.bg-card8 {
    background-image: url('/storage/assets/images/covers/bikes/8.jpg');
}
.bg-card9 {
    background-image: url('/storage/assets/images/covers/bikes/9.jpg');
}
.bg-card10 {
    background-image: url('/storage/assets/images/covers/bikes/10.jpg');
}
.bg-card11 {
    background-image: url('/storage/assets/images/covers/bikes/11.jpg');
}
.bg-card12 {
    background-image: url('/storage/assets/images/covers/bikes/12.jpg');
}
</style>
